/**
 * Жест «проверить вилку»: прочитать обе страницы контор прямо сейчас.
 *
 * Лента сама себе не доказательство. Зелёное «жива» имеет право появиться
 * только после текста на площадке. Молчание расширения — не «кэфы те же»,
 * а отказ подписать.
 *
 * Обе ноги уходят одним вопросом и читаются параллельно. Ждать дольше
 * семисот миллисекунд нельзя: вилка умрёт, пока кнопка думает.
 */

import { clipOf, parseClips, type PageClip } from './clip';
import { oddsForTab, pickTab, selForTab, wakeTabs, type TabHint } from './pages';
import { verdictOf, type State } from './verdict';

export const SNAP = 'forq:fork-snap';
export const SNAP_OK = 'forq:fork-snap:ok';
export const PAGE_TEXT = 'forq:page-text';
/** Полоса одной конторы будит все окна этой вилки. */
export const FORK_WAKE = 'forq:fork-wake';

/** Дольше кнопка не крутится: три секунды — уже труп. */
export const SNAP_WAIT_MS = 700;

export type SnapLeg = {
  venue: string;
  odds: string;
  url: string | null;
  /** Имена площадки: по ним улика отличает чужой матч. Старые вопросы без поля живы. */
  who?: string[];
  side?: string;
  /** Тройка конторы. По ней читаем ячейку Thunderpick, не весь текст страницы. */
  sel?: string;
  period?: string;
  kind?: string;
  line?: string;
  sport?: string;
  eventLine?: 'live' | 'prematch';
  marketKey?: string;
  name?: string;
};

export type SnapState = State | 'absent';

export type SnapOne = {
  venue: string;
  state: SnapState;
  ours: string;
  closest: number | null;
  /** Ячейка Thunderpick или ask полика. Нет — ближайшее число в тексте. */
  trusted?: boolean;
  /** Линия снята: кэф в closest ещё может стоять. */
  suspended?: boolean;
};

export type SnapReply = {
  legs: SnapOne[];
  /** Только проверка. Пульс клипы не кладёт. */
  clips?: PageClip[];
};

/** Текст страницы; на полике ещё лучший ask — не ближайшее число в innerText. */
export type PageSnap =
  | string
  | {
      text: string | null;
      book?: number | null;
      suspended?: boolean;
    }
  | null;

export function pageSnapOf(got: PageSnap): {
  text: string | null;
  book: number | null;
  suspended: boolean;
} {
  if (got === null) return { text: null, book: null, suspended: false };
  if (typeof got === 'string') return { text: got, book: null, suspended: false };
  const book = typeof got.book === 'number' && got.book > 1 ? got.book : null;
  return {
    text: typeof got.text === 'string' ? got.text : null,
    book,
    suspended: got.suspended === true,
  };
}

function withShut(row: SnapOne, shut: boolean): SnapOne {
  return shut ? { ...row, suspended: true } : row;
}

function rowOf(leg: SnapLeg, text: string | null, book: number | null, shut = false): SnapOne {
  if (book !== null) {
    const ours = Number(leg.odds);
    const apart = Number.isFinite(ours)
      ? Math.abs(Math.round(book * 100) - Math.round(ours * 100))
      : 999;
    return withShut(
      {
        venue: leg.venue,
        state: apart <= 1 ? 'match' : 'moved',
        ours: leg.odds,
        closest: book,
        trusted: true,
      },
      shut,
    );
  }
  if (text === null) {
    return withShut(
      { venue: leg.venue, state: 'unreadable', ours: leg.odds, closest: null },
      shut,
    );
  }
  const verdict = verdictOf(leg.odds, text);
  return withShut(
    {
      venue: leg.venue,
      state: verdict.state,
      ours: leg.odds,
      closest: verdict.closest,
      // Дамп всей страницы: на главной Positive / списке БК ближайшее число
      // — чужая строка. sel это не доказывает. Стол берёт только ячейку.
      trusted: false,
    },
    shut,
  );
}

export function snapEnvelope(
  id: string,
  legs: readonly SnapLeg[],
): { channel: string; id: string; legs: SnapLeg[] } {
  return { channel: SNAP, id, legs: [...legs] };
}

export function snapOkEnvelope(
  id: string,
  reply: SnapReply,
): { channel: string; id: string; legs: SnapOne[]; clips?: PageClip[] } {
  return reply.clips === undefined
    ? { channel: SNAP_OK, id, legs: reply.legs }
    : { channel: SNAP_OK, id, legs: reply.legs, clips: reply.clips };
}

/** Тот же допуск, что у обещания: Thunderpick `a~b~c`, Positive `id~positive~`. */
function snapSel(venue: string, sel: unknown): string | undefined {
  if (typeof sel !== 'string' || sel === '') return undefined;
  const parts = sel.split('~');
  if (venue === 'csgopositive' && /^\d+~positive~$/.test(sel)) return sel;
  if ((parts.length === 3 || parts.length === 4) && parts.every((bit) => bit !== '')) return sel;
  return undefined;
}

function idOf(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const id = (data as { id?: unknown }).id;
  return typeof id === 'string' && id.length > 0 && id.length <= 64 ? id : null;
}

export function parseSnap(data: unknown): { id: string; legs: SnapLeg[] } | null {
  if (typeof data !== 'object' || data === null) return null;
  if ((data as { channel?: unknown }).channel !== SNAP) return null;
  const id = idOf(data);
  const raw = (data as { legs?: unknown }).legs;
  if (id === null || !Array.isArray(raw) || raw.length === 0 || raw.length > 8) return null;
  const legs: SnapLeg[] = [];
  for (const one of raw) {
    if (typeof one !== 'object' || one === null) return null;
    const venue = (one as { venue?: unknown }).venue;
    const odds = (one as { odds?: unknown }).odds;
    const url = (one as { url?: unknown }).url;
    if (typeof venue !== 'string' || venue === '') return null;
    if (typeof odds !== 'string' || !/^\d+(\.\d+)?$/.test(odds)) return null;
    if (url !== null && typeof url !== 'string') return null;
    const who = (one as { who?: unknown }).who;
    const side = (one as { side?: unknown }).side;
    const names = Array.isArray(who)
      ? who.filter((name): name is string => typeof name === 'string' && name !== '').slice(0, 8)
      : undefined;
    const sel = snapSel(venue, (one as { sel?: unknown }).sel);
    const period = (one as { period?: unknown }).period;
    const kind = (one as { kind?: unknown }).kind;
    const line = (one as { line?: unknown }).line;
    const sport = (one as { sport?: unknown }).sport;
    const eventLine = (one as { eventLine?: unknown }).eventLine;
    const marketKey = (one as { marketKey?: unknown }).marketKey;
    const name = (one as { name?: unknown }).name;
    legs.push({
      venue,
      odds,
      url: url === '' ? null : url,
      ...(names !== undefined && names.length > 0 ? { who: names } : {}),
      ...(typeof side === 'string' && side !== '' ? { side } : {}),
      ...(sel !== undefined ? { sel } : {}),
      ...(typeof period === 'string' && period !== '' ? { period } : {}),
      ...(typeof kind === 'string' && /^(WIN|TTL|HCP|ITTL)$/i.test(kind)
        ? { kind: kind.toUpperCase() }
        : {}),
      ...(typeof line === 'string' && /^[+-]?\d+(\.\d+)?$/.test(line) ? { line } : {}),
      ...(typeof sport === 'string' && /^[a-z][a-z0-9_]{0,31}$/.test(sport) ? { sport } : {}),
      ...(eventLine === 'live' || eventLine === 'prematch' ? { eventLine } : {}),
      ...(typeof marketKey === 'string' && marketKey.length <= 256
        && /^[A-Z0-9_:.,+\-]+$/i.test(marketKey)
        ? { marketKey }
        : {}),
      ...(typeof name === 'string' && name !== '' && name.length <= 128 ? { name } : {}),
    });
  }
  return { id, legs };
}

export function parseSnapOk(data: unknown): { id: string; legs: SnapOne[]; clips?: PageClip[] } | null {
  if (typeof data !== 'object' || data === null) return null;
  if ((data as { channel?: unknown }).channel !== SNAP_OK) return null;
  const id = idOf(data);
  const raw = (data as { legs?: unknown }).legs;
  if (id === null || !Array.isArray(raw)) return null;
  const legs: SnapOne[] = [];
  for (const one of raw) {
    if (typeof one !== 'object' || one === null) return null;
    const venue = (one as { venue?: unknown }).venue;
    const state = (one as { state?: unknown }).state;
    const ours = (one as { ours?: unknown }).ours;
    const closest = (one as { closest?: unknown }).closest;
    if (typeof venue !== 'string' || venue === '') return null;
    if (state !== 'match' && state !== 'moved' && state !== 'unreadable' && state !== 'absent') {
      return null;
    }
    if (typeof ours !== 'string') return null;
    if (closest !== null && (typeof closest !== 'number' || !Number.isFinite(closest))) return null;
    const trusted = (one as { trusted?: unknown }).trusted;
    const suspended = (one as { suspended?: unknown }).suspended;
    legs.push({
      venue,
      state,
      ours,
      closest,
      ...(trusted === true ? { trusted: true } : trusted === false ? { trusted: false } : {}),
      ...(suspended === true ? { suspended: true } : {}),
    });
  }
  const extra = (data as { clips?: unknown }).clips;
  if (extra === undefined) return { id, legs };
  const clips = parseClips(extra);
  return clips === null ? { id, legs } : { id, legs, clips };
}

/**
 * Прочитать каждую ногу. Вкладки и чтение страницы снаружи: в тесте нет Chrome.
 * Ставки не кликает. Одну ногу не пропускает, даже если вкладки нет.
 */
export async function readLegs(
  legs: readonly SnapLeg[],
  tabs: readonly TabHint[],
  read: (tabId: number, odds: string, sel?: string) => Promise<PageSnap>,
): Promise<SnapOne[]> {
  return Promise.all(
    legs.map(async (leg) => {
      const tab = pickTab(tabs, leg.url);
      if (tab === null) {
        return { venue: leg.venue, state: 'absent', ours: leg.odds, closest: null };
      }
      const { text, book, suspended } = pageSnapOf(await read(tab.id, leg.odds, leg.sel));
      if (text === null && book === null) {
        return withShut(
          { venue: leg.venue, state: 'unreadable', ours: leg.odds, closest: null },
          suspended,
        );
      }
      return rowOf(leg, text, book, suspended);
    }),
  );
}

/**
 * Один проход: прочитать и покрасить все окна ног.
 *
 * `readLegs` берёт по одной вкладке на площадку — этого мало, когда открыты
 * три окна. Здесь каждое окно ног получает тот же вопрос, и лента по-прежнему
 * видит один вердикт на площадку. Повторно ту же вкладку не спрашиваем:
 * второй PAGE_TEXT мигал бы полосой без новой правды.
 */
function clipLeg(
  leg: SnapLeg,
  state: SnapOne['state'],
  closest: number | null,
  text: string | null,
  tabUrl?: string,
  suspended = false,
  trusted?: boolean,
): PageClip {
  return clipOf({
    venue: leg.venue,
    url: tabUrl ?? leg.url,
    odds: leg.odds,
    who: leg.who,
    side: leg.side,
    text,
    state,
    closest,
    ...(suspended ? { suspended: true } : {}),
    ...(trusted === true ? { trusted: true } : trusted === false ? { trusted: false } : {}),
  });
}

export async function snapAll(
  legs: readonly SnapLeg[],
  tabs: readonly TabHint[],
  read: (tabId: number, odds: string, sel?: string) => Promise<PageSnap>,
  opts?: { clip?: boolean },
): Promise<{ rows: SnapOne[]; painted: number[]; clips: PageClip[] }> {
  const wake = wakeTabs(
    tabs,
    legs.map((one) => one.url),
  );
  const texts = new Map<number, PageSnap>();
  const painted: number[] = [];
  await Promise.all(
    wake.map(async (tab) => {
      const odds = oddsForTab(tab.url, legs);
      if (odds === null) return;
      painted.push(tab.id);
      texts.set(tab.id, await read(tab.id, odds, selForTab(tab.url, legs)));
    }),
  );
  const wantClip = opts?.clip !== false;
  const packed = await Promise.all(
    legs.map(async (leg) => {
      const tab = pickTab(tabs, leg.url);
      if (tab === null) {
        const row = { venue: leg.venue, state: 'absent' as const, ours: leg.odds, closest: null };
        return { row, clip: wantClip ? clipLeg(leg, 'absent', null, null) : null };
      }
      let snap: PageSnap;
      if (texts.has(tab.id)) {
        snap = texts.get(tab.id) ?? null;
      } else {
        painted.push(tab.id);
        snap = await read(tab.id, leg.odds, leg.sel);
      }
      const { text, book, suspended } = pageSnapOf(snap);
      if (text === null && book === null) {
        const row = withShut(
          {
            venue: leg.venue,
            state: 'unreadable' as const,
            ours: leg.odds,
            closest: null,
          },
          suspended,
        );
        return {
          row,
          clip: wantClip ? clipLeg(leg, 'unreadable', null, null, tab.url, suspended) : null,
        };
      }
      const row = rowOf(leg, text, book, suspended);
      return {
        row,
        clip: wantClip
          ? clipLeg(leg, row.state, row.closest, text, tab.url, suspended, row.trusted)
          : null,
      };
    }),
  );
  return {
    rows: packed.map((one) => one.row),
    painted,
    clips: packed.map((one) => one.clip).filter((one): one is PageClip => one !== null),
  };
}

/** Два клика подряд — один проход, не дёрганье обеих полос дважды. */
export function once<T>(slot: { run: Promise<T> | null }, work: () => Promise<T>): Promise<T> {
  if (slot.run !== null) return slot.run;
  const run = work().finally(() => {
    if (slot.run === run) slot.run = null;
  });
  slot.run = run;
  return run;
}

export type SnapAsk = (legs: readonly SnapLeg[]) => Promise<SnapReply | null>;

let ask: SnapAsk | null = null;

/** Подмена для тестов карточки. Боевой путь — postMessage. */
export function setSnapAsk(next: SnapAsk | null): void {
  ask = next;
}

/** Тест подставил ответ — можно читать страницы без расширения. */
export function snapHooked(): boolean {
  return ask !== null;
}

let counter = 0;
const nextId = (): string => `${Date.now().toString(36)}-${(counter += 1)}`;

export async function snapPages(
  legs: readonly SnapLeg[],
  where: Window = window,
  wait = SNAP_WAIT_MS,
): Promise<SnapReply | null> {
  if (ask !== null) return ask(legs);
  if (typeof where.postMessage !== 'function' || legs.length === 0) return null;
  const id = nextId();
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      where.removeEventListener('message', hear);
      resolve(null);
    }, wait);
    function hear(event: MessageEvent): void {
      if (event.source !== where) return;
      const ok = parseSnapOk(event.data);
      if (ok === null || ok.id !== id) return;
      window.clearTimeout(timer);
      where.removeEventListener('message', hear);
      resolve({ legs: ok.legs, clips: ok.clips });
    }
    where.addEventListener('message', hear);
    try {
      where.postMessage(snapEnvelope(id, legs), '*');
    } catch {
      window.clearTimeout(timer);
      where.removeEventListener('message', hear);
      resolve(null);
    }
  });
}
